import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Alert, Button, Link, Stack, Typography } from '@mui/material';
import { CountryCode } from 'libphonenumber-js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import countryConfig, { CountryConfig } from 'src/utils/mrr/phone/countryConfig';
import * as Yup from 'yup';
import { RHFCheckbox, RHFSelect, RHFTextField } from '../../components/hook-form';
import FormProvider from '../../components/hook-form/FormProvider';
import Iconify from '../../components/iconify';
import { GuestGeneral, GuestType } from '../../models/GuestGeneral';
import {
  usePatchRegistrationStepMutation,
  usePostReservationGuestMutation,
} from '../../redux/rtkQuery/apiSlice';
import {
  checkAgeValid,
  convertInputTextToInt,
  handleIntegerInputChange,
} from '../../utils/mrr/ageUtils';
import { cloud_postReservationGuest } from '../../utils/mrr/cloudFunctions';
import { extractCountryConfig, validatePhoneNumber } from '../../utils/mrr/phone/phone';
import { EMAIL_REGEX } from '../../utils/mrr/uiConstants';
import { yupCheckPhone } from '../account/userSchemes';
import { StandardRTKQError } from '../error/StandardRTKQError';
import { RegistrationPicklistItem } from './types';
import { brandConfig } from '../../config';
import HelpLink from './HelpLinks';
import { LegalNameConfirmModal } from './LegalNameConfirmModal';
import { RegistrationFooter } from './RegistrationFooter';

interface RegistrationChangeHostFormProps {
  ageRequired: boolean;
  minAge: number;
  mainGuest: GuestGeneral | null;
  reservationName: string;
  callbackMainGuestChange: VoidFunction;
  callbackCancel: VoidFunction;
  callbackContinue: VoidFunction;
  optOutSMS: boolean;
}

interface FormInputProps {
  first_name: string;
  last_name: string;
  legal_name: string;
  email: string;
  phone: string;
  phone_consent: boolean;
  age: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

export function RegistrationChangeHostForm({
  ageRequired,
  minAge,
  mainGuest,
  reservationName,
  callbackMainGuestChange,
  callbackCancel,
  callbackContinue,
  optOutSMS,
}: RegistrationChangeHostFormProps) {
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [legalNameConfirmOpen, setLegalNameConfirmOpen] = useState(false);
  const confirmedLegalNameRef = useRef<string | null>(null);
  // const handleExpiredSession = useExpiredSessionErrorToNavigate();

  const resolver = useMemo(() => {
    return Yup.object().shape({
      first_name: Yup.string().required('First name is required.'),
      last_name: Yup.string().required('Last name is required.'),
      legal_name: Yup.string()
        .trim()
        .test(
          'full-legal-name',
          'Enter the full name as printed on your ID (first and last name).',
          (value) => {
            if (!value) return true;
            const tokens = value.split(/\s+/).filter(Boolean);
            return tokens.length >= 2 && tokens.every((t) => t.length >= 2);
          }
        ),
      email: Yup.string()
        .required('Email is required')
        .matches(EMAIL_REGEX, 'Please provide a valid email.'),
      phone: Yup.string()
        .required('Phone is required')
        .test('phone', 'Please provide a valid phone number.', yupCheckPhone),
      age: ageRequired
        ? Yup.string()
            .required('Host must be at least ' + minAge + ' years old at date of check-in.')
            .test(
              'age',
              //NOTE: The full "Host must be at least..." message is shown as a static info tip.
              'Must be at least ' + minAge,
              (value) => {
                const age = parseInt(value || '', 10);
                return checkAgeValid(age, minAge);
              }
            )
        : Yup.string().test('age', 'Age is invalid.', (value) => {
            const age = parseInt(value || '', 10);
            return checkAgeValid(age);
          }),
      address: Yup.string().required('Please provide an address.'),
      city: Yup.string().required('Please provide a city.'),
      state: Yup.string().nullable('Please provide a state/province.'),
      zipCode: Yup.string().required('Please provide a Zip/code.'),
      country: Yup.string().nullable(),
    });
  }, [ageRequired, minAge]);

  const formDefaults = {
    first_name: '',
    last_name: '',
    legal_name: '',
    email: '',
    phone: '',
    phone_consent: optOutSMS,
    age: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
  };

  const methods = useForm<FormInputProps>({
    defaultValues: formDefaults,
    resolver: yupResolver(resolver),
    mode: 'all',
  });

  const {
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid, dirtyFields, errors },
    trigger,
    setError,
    getFieldState,
    setValue,
    setFocus,
    watch,
  } = methods;

  const savedLegalName = '';

  const values = watch();
  const [stateOptions, setStateOptions] = useState<CountryConfig>();

  // drives the PROVINCE/STATE fields by watching changes to country
  useEffect(() => {
    if (values.country) {
      const options = countryConfig.find(
        (country) => country.code === values.country && country.divisions
      );
      setStateOptions(options);
      if (!options) {
        setValue('state', '');
      }
    } else if (stateOptions) {
      setValue('state', '');
      setStateOptions(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.country]);

  const [
    sendPatchRegistrationStep,
    {
      isLoading: patchStepIsLoading,
      isError: patchStepIsError,
      error: patchStepError,
      reset: resetPatchStepMutation,
    },
  ] = usePatchRegistrationStepMutation();

  const completeMainGuestStep = useCallback(async () => {
    const sfPayload = {
      reservationName: reservationName,
      registrationStep: RegistrationPicklistItem.VERIFY_HOST,
    };

    await sendPatchRegistrationStep(sfPayload)
      .then((data: any) => {
        if (data.error) {
          //TODO: Double check this step. Errors were already handled on this page before a refactor.
          console.error(data.error.message);
          return;
        }
        callbackContinue();
      })
      .catch((putError: any) => {
        console.error('error on update main guest step', putError);
      });

    //TODO
    // sendEvent(new GAEventGuest(
    //     sfPayload.type,
    //     sfPayload.is_minor,
    //     sfPayload.first_name,
    //     sfPayload.last_name,
    //     true,
    //     sfPayload.relation,
    //     reservation_name,
    // ))
  }, [callbackContinue, reservationName, sendPatchRegistrationStep]);

  const [
    sendPostReservationGuest,
    {
      isLoading: postGuestIsLoading,
      isError: postGuestIsError,
      error: postGuestError,
      reset: resetPostGuestMutation,
    },
  ] = usePostReservationGuestMutation();

  // const { sendEvent } = useAnalyticsContext()

  const onSubmit = handleSubmit(
    async ({
      first_name,
      last_name,
      legal_name,
      email,
      phone,
      phone_consent,
      age,
      address,
      city,
      state,
      zipCode,
      country,
    }) => {
      const trimmedLegalName = legal_name?.trim() ?? '';
      const needsConfirm =
        !!trimmedLegalName &&
        trimmedLegalName !== savedLegalName &&
        trimmedLegalName !== confirmedLegalNameRef.current;
      if (needsConfirm) {
        setLegalNameConfirmOpen(true);
        return;
      }

      const sfPayload: Record<string, any> = {
        hostMode: true,
        reservationName: reservationName,
        first_name,
        last_name,
        email,
        phone,
        messaging_channels: null,
        age,
        address: {
          street: address,
          postal_code: zipCode,
          city,
          state,
          country,
        },
        type: GuestType.Host,
      };
      if (legal_name?.trim()) sfPayload.legal_name = legal_name.trim();

      if (phone) {
        const extractedCountryConfig = extractCountryConfig(phone);
        let sfPhone = '';
        const phoneResult = validatePhoneNumber(
          phone || '',
          extractedCountryConfig.code as CountryCode
        );
        if (phoneResult.success) {
          sfPhone = phoneResult.parsedPhone.formatInternational();
        }
        if (sfPhone) {
          sfPayload.phone = sfPhone;
        }
        if (phone_consent) {
          sfPayload.messaging_channels = ['sms'];
        } else {
          sfPayload.messaging_channels = null;
        }
      }

      // CREATE NEW
      await sendPostReservationGuest(sfPayload)
        .then(async (data: any) => {
          if (data.error) {
            setGlobalError(data.error.message);
            return;
          }

          await completeMainGuestStep();

          callbackMainGuestChange();
          callbackContinue();
        })
        .catch((postError: any) => {
          console.error('error on create main guest', postError);
        });

      //TODO
      // sendEvent(new GAEventGuest(
      //     sfPayload.type,
      //     sfPayload.is_minor,
      //     sfPayload.first_name,
      //     sfPayload.last_name,
      //     true,
      //     sfPayload.relation,
      //     reservation_name,
      // ))
    }
  );

  const { age } = watch();

  // this drives real-time validation, since we override the age inputs on-change
  useEffect(() => {
    //TODO: This is not complete. We don't always get the current dirty fields.
    //      It solves the init-form case, but it can break the as-you-type case
    //      (which this is intended to solve).
    if (!dirtyFields.age) {
      // We don't want this input to initialize with an error, which would happen
      // without this check, because we're manually triggering validation.
      return;
    }

    trigger('age');
  }, [age, dirtyFields.age, trigger]);

  const showStandardError = postGuestIsError && !globalError;
  const showGlobalError = globalError;

  const formLoading = isSubmitting || postGuestIsLoading;

  return (
    <FormProvider
      name="Change Host Form"
      id={`Change Host Form: ${reservationName}`}
      methods={methods}
      onSubmit={onSubmit}
    >
      <Stack spacing={2}>
        {showStandardError && (
          <StandardRTKQError
            error={postGuestError}
            endpoint={cloud_postReservationGuest}
            mutationCall
          />
        )}
        {showGlobalError && <Alert severity="error">{globalError}</Alert>}
        <RHFTextField
          fullWidth
          name="first_name"
          label="First Name"
          autoComplete="given-name"
          inputProps={{ 'aria-required': true }}
        />
        <RHFTextField
          fullWidth
          name="last_name"
          label="Last Name"
          autoComplete="family-name"
          inputProps={{ 'aria-required': true }}
        />
        <RHFTextField
          fullWidth
          name="legal_name"
          label="Legal Name"
          helperText="Enter the full name for the new host as printed on their ID (first and last). Leave blank if the name entered above matches their ID."
        />
        <RHFTextField
          name="email"
          type="email"
          label="Email"
          autoComplete="email"
          inputProps={{ 'aria-required': true }}
        />
        <RHFTextField
          name="phone"
          type="tel"
          label="Phone"
          autoComplete="tel"
          inputProps={{ 'aria-required': true }}
        />
        <RHFCheckbox
          name="phone_consent"
          labelPlacement="end"
          dense
          label={
            <Typography variant="body1">
              {optOutSMS ? 'Receive updates via text' : 'Receive updates via text (Recommended)'}
            </Typography>
          }
          helperText={
            <Typography variant="caption" color="text.secondary">
              When you choose to receive updates via text you agree to receive important SMS updates
              related to your reservation with {brandConfig.name}. Message frequency varies. Message
              and data rates may apply. Text HELP for help. Text STOP to opt out. See our{' '}
              <Link
                href={brandConfig.helpLinks.privacy_policy}
                target="_blank"
                className="text-secondary-main underline hover:text-secondary-light"
              >
                Privacy Policy
              </Link>
            </Typography>
          }
        />
        <RHFTextField
          fullWidth
          name="age"
          label="Age"
          inputMode="numeric"
          inputProps={{ 'aria-required': true, inputMode: 'numeric', pattern: '[0-9]*' }}
          onChange={(e) => {
            const parsedValue = handleIntegerInputChange(e);
            setValue('age', convertInputTextToInt(parsedValue));
            trigger('age');
          }}
        />
        {ageRequired && (
          <Stack alignItems="flex-start" direction="row" spacing={2}>
            <Iconify icon="flat-color-icons:info" width={16} />
            <Typography fontSize={12} variant="caption" color="text.secondary">
              {'Host must be at least ' + minAge + ' years old at date of check-in.'}
            </Typography>
          </Stack>
        )}
        <RHFTextField
          type="text"
          name="address"
          label="Address"
          autoComplete="street-address"
          inputProps={{ 'aria-required': true }}
        />
        <RHFTextField
          type="text"
          name="city"
          label="City"
          autoComplete="address-level2"
          inputProps={{ 'aria-required': true }}
        />
        {stateOptions && stateOptions.divisions && (
          <RHFSelect
            native
            name="state"
            label={stateOptions.divisionLabel}
            inputProps={{ autoComplete: 'address-level1' }}
          >
            <option value="">Select {stateOptions.divisionLabel}</option>
            {stateOptions.divisions.map((optionObj) => (
              <option key={optionObj.name} value={optionObj.name}>
                {optionObj.name}
              </option>
            ))}
          </RHFSelect>
        )}
        <RHFTextField
          type="text"
          name="zipCode"
          label="Zip/Code"
          autoComplete="postal-code"
          inputProps={{ 'aria-required': true }}
        />
        <RHFSelect
          native
          name="country"
          label="Country"
          placeholder="Country"
          inputProps={{ autoComplete: 'country' }}
          onInput={(event: any) => {
            setValue('country', event.target.value);
          }}
        >
          <option value="">Select country</option>
          {countryConfig.map((country) => (
            <option key={country.code} value={country.code}>
              {country.name}
            </option>
          ))}
        </RHFSelect>
        {brandConfig.helpLinks.guest_registration && (
          <HelpLink label="guest_registration" value={brandConfig.helpLinks.guest_registration} />
        )}
        <RegistrationFooter
          backButton={
            <Button disabled={isSubmitting} onClick={callbackCancel}>
              Cancel
            </Button>
          }
        >
          <LoadingButton
            type="submit"
            loading={formLoading}
            disabled={!isValid}
            variant="contained"
          >
            Change Host
          </LoadingButton>
        </RegistrationFooter>
      </Stack>
      <LegalNameConfirmModal
        open={legalNameConfirmOpen}
        legalName={values.legal_name?.trim() ?? ''}
        loading={formLoading}
        forNewHost
        onEdit={() => {
          setLegalNameConfirmOpen(false);
          setFocus('legal_name');
        }}
        onConfirm={() => {
          confirmedLegalNameRef.current = values.legal_name?.trim() ?? '';
          setLegalNameConfirmOpen(false);
          onSubmit();
        }}
      />
    </FormProvider>
  );
}
